<template>
  <div ref="mapContainer" class="w-full h-full" />
</template>

<script>
export default {
  name: 'MapView',

  props: {
    mapboxToken: {
      type: String,
      required: true
    },
    gyms: {
      type: Array,
      default: () => []
    },
    selectedGym: {
      type: Object,
      default: null
    }
  },

  emits: ['marker-click', 'map-click'],

  data() {
    return {
      map: null,
      markers: [],
      mapboxgl: null
    }
  },

  watch: {
    gyms: {
      handler() {
        this.updateMarkers()
      },
      deep: true
    },

    selectedGym(gym) {
      this.highlightMarker(gym)
    }
  },

  async mounted() {
    await this.loadMapboxGL()
    this.initMap()
  },

  beforeUnmount() {
    this.markers.forEach((m) => m.remove())
    this.markers = []
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },

  methods: {
    async loadMapboxGL() {
      // Mapbox GL JS loaded from CDN via importmap
      if (window.mapboxgl) {
        this.mapboxgl = window.mapboxgl
        return
      }

      // Fallback: dynamically load from CDN
      await new Promise((resolve, reject) => {
        // Load CSS
        if (!document.querySelector('link[href*="mapbox-gl"]')) {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css'
          document.head.appendChild(link)
        }

        // Load JS
        if (!document.querySelector('script[src*="mapbox-gl"]')) {
          const script = document.createElement('script')
          script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js'
          script.onload = () => {
            this.mapboxgl = window.mapboxgl
            resolve()
          }
          script.onerror = reject
          document.head.appendChild(script)
        } else {
          this.mapboxgl = window.mapboxgl
          resolve()
        }
      })
    },

    initMap() {
      if (!this.mapboxgl || !this.mapboxToken) return

      this.mapboxgl.accessToken = this.mapboxToken

      this.map = new this.mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [-98.5795, 39.8283], // Center of US
        zoom: 4
      })

      this.map.addControl(new this.mapboxgl.NavigationControl(), 'top-left')

      this.map.on('click', (e) => {
        // Only emit if not clicking a marker
        if (!e.originalEvent.target.closest('.gym-marker')) {
          this.$emit('map-click', e)
        }
      })

      this.map.on('load', () => {
        this.updateMarkers()
      })
    },

    updateMarkers() {
      // Remove existing markers
      this.markers.forEach((m) => m.remove())
      this.markers = []

      if (!this.map || !this.mapboxgl) return

      this.gyms.forEach((gym) => {
        if (!gym.latitude || !gym.longitude) return

        const el = document.createElement('div')
        el.className = 'gym-marker'
        el.style.cssText = 'width: 24px; height: 24px; cursor: pointer;'

        const dot = document.createElement('div')
        dot.style.cssText = `
          width: 100%; height: 100%; border-radius: 50%;
          border: 2px solid #171717;
          background: ${this.markerColor(gym.status)};
          transition: transform 0.15s ease;
        `
        el.appendChild(dot)

        el.addEventListener('mouseenter', () => {
          dot.style.transform = 'scale(1.3)'
        })
        el.addEventListener('mouseleave', () => {
          dot.style.transform = 'scale(1)'
        })

        const marker = new this.mapboxgl.Marker({ element: el })
          .setLngLat([gym.longitude, gym.latitude])
          .addTo(this.map)

        el.addEventListener('click', (e) => {
          e.stopPropagation()
          this.$emit('marker-click', gym)
        })

        this.markers.push(marker)
      })

      // Fit bounds if gyms exist
      if (this.gyms.length > 0) {
        const bounds = new this.mapboxgl.LngLatBounds()
        let hasValidCoords = false
        this.gyms.forEach((gym) => {
          if (gym.latitude && gym.longitude) {
            bounds.extend([gym.longitude, gym.latitude])
            hasValidCoords = true
          }
        })
        if (hasValidCoords) {
          this.map.fitBounds(bounds, { padding: 50, maxZoom: 14 })
        }
      }
    },

    highlightMarker(gym) {
      this.markers.forEach((marker, index) => {
        const el = marker.getElement()
        const dot = el.querySelector('div')
        if (!dot) return
        if (gym && this.gyms[index]?.id === gym.id) {
          dot.style.transform = 'scale(1.5)'
          el.style.zIndex = '10'
        } else {
          dot.style.transform = 'scale(1)'
          el.style.zIndex = '1'
        }
      })
    },

    flyTo(lng, lat, zoom = 14) {
      if (this.map) {
        this.map.flyTo({ center: [lng, lat], zoom, duration: 1500 })
      }
    },

    markerColor(status) {
      const colors = {
        approved: '#22c55e',
        pending: '#f59e0b',
        rejected: '#ef4444'
      }
      return colors[status] || '#94a3b8'
    }
  }
}
</script>
